/**
 * Departure place picker for bookings: the switched-on addresses from the
 * predefined list. The empty option stands for "the default" (the trip's usual
 * place when booking, no place when editing). A place the booking already has
 * that is no longer in the list stays selectable, so opening and saving a
 * booking never silently drops it.
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { BookingDeparture } from "@common/types";
import { getLocations } from "../services/api";

export default function DeparturePlaceSelect({
  id,
  value,
  onChange,
  emptyLabel,
  current,
}: {
  id: string;
  /** The chosen location id; "" = the default */
  value: string;
  onChange: (locationId: string) => void;
  emptyLabel: string;
  /** The departure the booking already has (edit only) */
  current?: BookingDeparture;
}) {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: ["locations"], queryFn: getLocations });
  const locations = (data?.data ?? []).filter((l) => l.isActive);
  const missing = current?.locationId && !locations.some((l) => l.id === current.locationId) ? current : undefined;

  return (
    <div>
      <label htmlFor={id} className="label">
        {t("bookingForm.departure.label")}
      </label>
      <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{emptyLabel}</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>
            {l.address ? `${l.name} - ${l.address}` : l.name}
          </option>
        ))}
        {missing && (
          <option value={missing.locationId}>{t("bookingForm.departure.notInList", { name: missing.name })}</option>
        )}
      </select>
    </div>
  );
}
